import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, ArrowLeft, Clock, Dumbbell, CheckCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramRoutine = () => {
  const { level, day } = useParams();
  const [routine, setRoutine] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);

  useEffect(() => {
    fetchData();
  }, [level, day]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    
    try {
      // Obtener usuario
      if (token) {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);
      }

      // Obtener rutina
      const routineRes = await axios.get(`${BACKEND_URL}/api/program-content/${level}/routine/${day}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRoutine(routineRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('no_access');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleExerciseComplete = (index) => {
    if (completedExercises.includes(index)) {
      setCompletedExercises(completedExercises.filter(i => i !== index));
    } else {
      setCompletedExercises([...completedExercises, index]);
    }
  };

  const getLevelName = () => {
    const names = {
      'nivel_1': 'Nivel 1 - Principiante',
      'nivel_2': 'Nivel 2 - Intermedio',
      'nivel_3': 'Nivel 3 - Avanzado'
    };
    return names[level] || level;
  };

  const getLevelColor = () => {
    const colors = {
      'nivel_1': 'bg-blue-600',
      'nivel_2': 'bg-purple-600',
      'nivel_3': 'bg-orange-600'
    };
    return colors[level] || 'bg-pink-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error === 'no_access') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-3xl p-8 max-w-md text-center border border-zinc-800">
          <Lock className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 text-white">Contenido Bloqueado</h1>
          <p className="text-zinc-400 mb-6">
            No tienes acceso a este nivel. Actualiza tu suscripción para desbloquear este contenido.
          </p>
          <Link to="/programs">
            <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8">
              Ver Programas
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${getLevelColor()}`}>
              {getLevelName()}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            {routine?.title || `Día ${day} - Entrenamiento`}
          </h1>
          
          {routine?.description && (
            <p className="text-zinc-400 mt-2 whitespace-pre-line">{routine.description}</p>
          )}
        </div>

        {/* Videos - Nuevo formato con array de videos */}
        {routine?.videos && routine.videos.length > 0 && (
          <div className="space-y-6 mb-8">
            {routine.videos.map((video, idx) => {
              // Convertir URL de Google Drive a formato embebible
              const getEmbedUrl = (url) => {
                if (url.includes('youtube') || url.includes('youtu.be')) {
                  return {
                    type: 'youtube',
                    url: url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
                  };
                }
                if (url.includes('drive.google.com')) {
                  // Extraer el ID del archivo de Drive
                  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
                  if (match) {
                    return {
                      type: 'drive',
                      url: `https://drive.google.com/file/d/${match[1]}/preview`
                    };
                  }
                }
                return { type: 'video', url };
              };
              
              const embed = getEmbedUrl(video.video_url);
              
              return (
                <div key={idx} className="bg-zinc-900 rounded-3xl overflow-hidden border border-zinc-800">
                  <div className="p-4 border-b border-zinc-800">
                    <h3 className="font-bold text-lg text-white">{video.title || `Ejercicio ${idx + 1}`}</h3>
                    {video.description && <p className="text-sm text-zinc-400 mt-1 whitespace-pre-line">{video.description}</p>}
                  </div>
                  <div className="aspect-video bg-black flex items-center justify-center">
                    {embed.type === 'youtube' || embed.type === 'drive' ? (
                      <iframe
                        src={embed.url}
                        className="w-full h-full"
                        allowFullScreen
                        allow="autoplay; encrypted-media"
                        title={video.title}
                      />
                    ) : (
                      <video
                        src={embed.url.startsWith('http') ? embed.url : `${BACKEND_URL}${embed.url}`}
                        controls
                        className="w-full h-full"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Video - Formato antiguo (compatibilidad) */}
        {!routine?.videos?.length && routine?.video_url && (
          <div className="bg-zinc-900 rounded-3xl overflow-hidden border border-zinc-800 mb-8">
            <div className="aspect-video bg-black flex items-center justify-center">
              <video
                src={routine.video_url.startsWith('http') ? routine.video_url : `${BACKEND_URL}${routine.video_url}`}
                controls
                className="w-full h-full"
              />
            </div>
          </div>
        )}

        {/* Sin contenido */}
        {(!routine?.exercises || routine.exercises.length === 0) && !routine?.video_url && !routine?.videos?.length && (
          <div className="bg-zinc-900 rounded-3xl p-8 text-center border border-zinc-800">
            <Dumbbell className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2 text-white">Contenido Próximamente</h2>
            <p className="text-zinc-400">
              La rutina para este día aún está siendo preparada. ¡Vuelve pronto!
            </p>
          </div>
        )}

        {/* Lista de ejercicios */}
        {routine?.exercises && routine.exercises.length > 0 && (
          <div className="bg-zinc-900 rounded-3xl border border-zinc-800 overflow-hidden">
            <div className="p-6 border-b border-zinc-800">
              <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                <Dumbbell className="w-5 h-5 text-pink-500" />
                Ejercicios ({routine.exercises.length})
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Marca cada ejercicio cuando lo completes
              </p>
            </div>
            
            <div className="divide-y divide-zinc-800">
              {routine.exercises.map((exercise, idx) => (
                <div 
                  key={idx}
                  onClick={() => toggleExerciseComplete(idx)}
                  className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                    completedExercises.includes(idx) ? 'bg-green-500/10' : 'hover:bg-zinc-800'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    completedExercises.includes(idx) 
                      ? 'bg-green-500 text-white' 
                      : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {completedExercises.includes(idx) ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <span className="font-bold">{idx + 1}</span>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={`font-bold ${completedExercises.includes(idx) ? 'line-through text-zinc-500' : 'text-white'}`}>
                      {exercise.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1">
                      {exercise.sets && <span>{exercise.sets} series</span>}
                      {exercise.reps && <span>{exercise.reps} reps</span>}
                      {exercise.notes && <span className="text-pink-500">{exercise.notes}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Progreso */}
            <div className="p-6 bg-zinc-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">Progreso</span>
                <span className="text-sm text-zinc-400">
                  {completedExercises.length} / {routine.exercises.length} ejercicios
                </span>
              </div>
              <div className="w-full bg-zinc-700 rounded-full h-2">
                <div 
                  className="bg-pink-500 h-2 rounded-full transition-all"
                  style={{ width: `${(completedExercises.length / routine.exercises.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Navegación entre días */}
        <div className="flex justify-between mt-8">
          {parseInt(day) > 1 && (
            <Link to={`/routine/${level}/${parseInt(day) - 1}`}>
              <Button variant="outline" className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Día {parseInt(day) - 1}
              </Button>
            </Link>
          )}
          <div className="flex-1" />
          {/* Limitar días según nivel: nivel_1=4, nivel_2=5, nivel_3=6 */}
          {(() => {
            const maxDays = { 'nivel_1': 4, 'nivel_2': 5, 'nivel_3': 6 };
            const max = maxDays[level] || 4;
            return parseInt(day) < max && (
              <Link to={`/routine/${level}/${parseInt(day) + 1}`}>
                <Button variant="outline" className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  Día {parseInt(day) + 1}
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </Button>
              </Link>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProgramRoutine;
